import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, RouterModule, UrlSegment } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, map } from 'rxjs';

import { stateMap } from '@digital-platform/rs-addresses/domain';
import { BreadcrumbItem, Facility } from '@digital-platform/rs-cms/domain';
import { RichTextLinkComponent } from '@digital-platform/rs-cms/feature-ptypes';
import { CmsFacilitiesService } from '@digital-platform/rs-cms/services';
import { FacilityLocation } from '@digital-platform/rs-maps/domain';
import { BingMapComponent } from '@digital-platform/rs-maps/feature-bing-map';
import { PageMetaData } from '@digital-platform/rs-meta/domain';
import { MetaDataService } from '@digital-platform/rs-meta/services';
import { BreadcrumbsComponent } from '@digital-platform/rs-shared/ui';
import {
  FilterByLocationComponent,
  LocationList,
  statesideFacilites,
  intlFacilities,
} from '@digital-platform/rs-shared/ui';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';
import { FacilityFilterServiceComponent } from '../facility-filter-service/facility-filter-service.component';
import { FacilityItemComponent } from '../facility-item/facility-item.component';
import { FacilityResultsComponent } from '../facility-results/facility-results.component';
import { FacilityTabsComponent } from '../facility-tabs/facility-tabs.component';
import { serviceCodeMap, serviceNameMap } from './facilities.constants';

@UntilDestroy()
@Component({
  selector: 'digital-platform-facilities',
  imports: [
    CommonModule,
    RouterModule,
    BreadcrumbsComponent,
    RichTextLinkComponent,
    FacilityItemComponent,
    IconComponent,
    FacilityTabsComponent,
    FacilityResultsComponent,
    FacilityFilterServiceComponent,
    BingMapComponent,
    FilterByLocationComponent,
  ],
  standalone: true,
  templateUrl: './facilities.component.html',
  styleUrls: ['./facilities.component.scss'],
})
export class FacilitiesComponent implements OnInit {
  @ViewChild('serviceBadges') public serviceBadges!: ElementRef;
  @ViewChild('facilityFilterService')
  public filterService!: FacilityFilterServiceComponent;
  public usaLocations: LocationList[] = statesideFacilites;
  public intlLocations: LocationList[] = intlFacilities;
  public selectedServiceNames: string[] = [];
  public availableServices: Record<string, boolean> = {};
  public stateKey: string | undefined = undefined;
  public pushpinCoordinates$: BehaviorSubject<Facility[] | FacilityLocation[]> =
    new BehaviorSubject<Facility[] | FacilityLocation[]>([]);
  public breadcrumbs: BreadcrumbItem[] = [
    {
      type: 'breadcrumbItem',
      status: true,
      linkTitle: 'Find a Facility',
      linkUrl: '',
    },
  ];
  public length = 0;
  public serviceBadgesHeight = 0;
  public facilities$: Observable<Facility[]> | undefined = undefined;
  public filteredFacilities: Facility[] = [];

  private cmsFacilitiesService = inject(CmsFacilitiesService);
  private cdRef = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  private locationChange = false;
  private metaDataService = inject(MetaDataService);
  private selectedServiceNames$ = new BehaviorSubject<string[]>([]);
  private city = '';
  private currentState = {
    facilities: [] as Facility[],
    selectedServiceNames: [] as string[],
    pushpinCoordinates: [] as Facility[],
    browseLocation: [] as UrlSegment[],
  };

  public ngOnInit(): void {
    this.setMetaData();
    this.getLocation();

    this.selectedServiceNames$
      .pipe(untilDestroyed(this))
      .subscribe((selectedServiceNames) => {
        this.selectedServiceNames = selectedServiceNames;
        this.filterFacilities();
      });

    this.pushpinCoordinates$
      .pipe(untilDestroyed(this))
      .subscribe((pushpinCoordinates) => {
        this.currentState.pushpinCoordinates = pushpinCoordinates as Facility[];
        this.filterFacilities();
        this.cdRef.detectChanges();
      });
  }

  public getLocation() {
    this.route.url.pipe(untilDestroyed(this)).subscribe((browseLocation) => {
      const location =
        browseLocation.length > 0
          ? browseLocation[0].parameters['location']
          : null;

      this.currentState.browseLocation = browseLocation;

      if (location) {
        this.updateStateKey(location, true);
      }
      this.getFacilities();
    });
  }

  private updateStateKey(location: string, clearFilters = false): void {
    const state = Object.keys(stateMap).find(
      (key) => stateMap[key] === location
    );

    // NOTE: We don't have Mexico locations anymore, but I'm afraid to touch this. Revisit if we add more international facilities.
    // check if location is in Mexico
    if (!state && this.intlLocations[1].list.includes(location)) {
      this.stateKey = 'MX';
      this.city = location;
      this.locationChange = true;
    } else if (state !== this.stateKey) {
      this.stateKey = state;
      this.locationChange = true;
    }

    if (
      clearFilters &&
      this.locationChange &&
      this.selectedServiceNames.length > 0
    ) {
      this.clearFilters(this.filterService);
    }
  }

  private setMetaData(): void {
    const metaData: PageMetaData = {
      title: 'Environmental Solutions Facility',
      description:
        'Find a Republic Services environmental solutions facility near you. We have options to meet your disposal needs & help you achieve your goals.',
      canonical_url: 'https://www.republicservices.com/${this.router.url}',
    };

    this.metaDataService.setMetaData(metaData);
  }

  private filterFacilities(): void {
    const { facilities, pushpinCoordinates, browseLocation } =
      this.currentState;

    let locationFacilities: Facility[] = [];
    let mapFacilities: Facility[] = [];
    let serviceFacilities: Facility[] = [];

    const location =
      browseLocation.length > 0
        ? browseLocation[0].parameters['location']
        : null;
    if (location) {
      if (this.locationChange && this.selectedServiceNames.length > 0) {
        this.currentState.selectedServiceNames = [];
      }
      locationFacilities = this.filterByLocation(facilities, location);
    } else {
      this.stateKey = undefined;
    }

    mapFacilities = this.filterByMapView(facilities, pushpinCoordinates);

    serviceFacilities = this.filterByService(
      facilities,
      this.convertServiceName(this.selectedServiceNames)
    );

    const nonEmptyArrays = [locationFacilities, serviceFacilities].filter(
      (arr) => arr.length > 0
    );

    // If no facilities in map view, the other filters don't matter
    if (mapFacilities.length === 0) {
      this.filteredFacilities = [];
      return;
    }

    // If route is not changing, then we include the map facilities (when route changes, the map view will change, so we disregard map facilities)
    if (!this.locationChange) {
      nonEmptyArrays.push(mapFacilities);
    }

    // If there's only one non-empty array, assign it to filteredFacilities
    if (nonEmptyArrays.length === 1) {
      this.filteredFacilities = nonEmptyArrays[0];
      return;
    }

    // If filtering by state and user goes back to view all facilities, display them all
    if (nonEmptyArrays.length === 0) {
      this.filteredFacilities = facilities;
      return;
    }

    this.filteredFacilities = nonEmptyArrays.reduce((acc, currentArray) =>
      acc.filter((accItem) =>
        currentArray.some(
          (currentItem) => currentItem.facility_name === accItem.facility_name
        )
      )
    );
  }

  private filterByLocation(
    facilities: Facility[],
    location: string
  ): Facility[] {
    this.updateStateKey(location);

    return facilities.filter((facility: Facility) => {
      if (this.stateKey !== 'MX') {
        return facility.state === this.stateKey;
      } else {
        return facility.city === this.city;
      }
    });
  }

  public filterByMapView(
    facilities: Facility[],
    excludedPushpinCoordinates: Facility[]
  ): Facility[] {
    if (excludedPushpinCoordinates.length === 0) return facilities;
    return facilities.filter(
      (facility: Facility) =>
        facility.latitude !== null &&
        facility.longitude !== null &&
        !excludedPushpinCoordinates.some(
          (coord) =>
            String(coord.latitude) === facility.latitude &&
            String(coord.longitude) === facility.longitude
        )
    );
  }

  public filterByService(
    facilities: Facility[],
    services: string[]
  ): Facility[] {
    const filteredByServices = facilities.filter((facility: Facility) => {
      const facilityServicePins = facility.pin_categories
        ?.split(',')
        .map((pin: string | null) => pin?.trim());
      return services.some((selected) =>
        facilityServicePins?.includes(selected)
      );
    });
    return filteredByServices;
  }

  private convertServiceName(serviceNames: string[]): string[] {
    return serviceNames.map((name) => serviceNameMap[name] || '');
  }

  public updateSelectedServices(selectedServices: string[]) {
    this.selectedServiceNames$.next([...selectedServices]);
    this.updateServicesAvailability();
    this.cdRef.detectChanges();

    if (this.serviceBadges && this.serviceBadges.nativeElement) {
      this.serviceBadgesHeight = this.serviceBadges.nativeElement.offsetHeight;
    }
  }

  public updateServicesAvailability(): void {
    const servicesAvailability: Record<string, boolean> = {};
    Object.keys(serviceCodeMap).forEach((name) => {
      this.facilities$
        ?.pipe(
          untilDestroyed(this),
          map((facilities) =>
            facilities.some((facility: Facility) =>
              facility.pin_categories?.includes(name)
            )
          )
        )
        .subscribe((result: boolean): void => {
          servicesAvailability[serviceCodeMap[name]] = result;
        });
    });
    this.availableServices = servicesAvailability;
  }

  public clearFilters(
    facilityFilterServices: FacilityFilterServiceComponent
  ): void {
    this.selectedServiceNames$.next([]);
    this.availableServices = {};
    this.serviceBadgesHeight = 0;
    facilityFilterServices.clearAll();
  }

  scrollTo(selector?: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!selector) {
      window.scrollTo({ top: 1200, behavior: 'smooth' });
    } else {
      const element = document.querySelector('.' + selector);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private getFacilities() {
    this.updateFacilitiesObservable();
    this.facilities$?.pipe(untilDestroyed(this)).subscribe((facilities) => {
      this.currentState.facilities = facilities;
      this.metaDataService.setMetaData({
        coveo_category: facilities?.[0]?.coveo_category
          ?.map(({ name }) => name)
          ?.join(', '),
      });
      this.filterFacilities();
    });
  }

  private updateFacilitiesObservable(): void {
    this.facilities$ = this.cmsFacilitiesService
      .getFacilitiesContent(this.stateKey)
      .pipe(
        untilDestroyed(this),
        map((facilities) => {
          const sortedFacilities = [...facilities];
          sortedFacilities.sort((a, b) =>
            a.facility_name.localeCompare(b.facility_name)
          );
          this.length = sortedFacilities.length;
          return sortedFacilities;
        })
      );
  }
}
